import React, { useState, useEffect } from 'react'

import { DEFAULT, PRODUCT_PAGE, FULFILLED, REJECTED, CART } from '../../../constants/constants'
import { getCheckoutcart } from '../../../store/requests/apiRequests/getCheckoutcart'
import {
  setIsAddToCartPromptModalOpen,
  setIsAddToCartConfirmedModalOpen,
  setIsAddToCartAdditionalPromptModalOpen,
  setIsAddToCartErrorModalOpen,
  setIsAddToCartUnavailableModalOpen,
  setIsMobileMenuOpen,
  setIsSummaryPreviewExpanded,
} from '../../../store/ui/uiSlice'
import ArticlesList from '../../ArticlesList/ArticlesList'
import Heading from '../../Heading/Heading'
import Modal from '../../Modal/Modal'
import * as commonStyles from '../../styles'
import * as styles from './styles'
import { useAppDispatch, useAppSelector } from '../../../hooks'

const Styled = {
  ...commonStyles,
  ...styles,
}

interface Props {
  onRequestSaveProject: (args: { type: string }) => {
    internalSaveUuidv4: string | null
  }
  insideSummaryPanel?: boolean
}

const AddToCart: React.FC<Props> = ({ onRequestSaveProject, insideSummaryPanel }) => {
  const dispatch = useAppDispatch()

  const { errors: bundleErrors, articlesTotal } = useAppSelector((state) => state.bundle)
  const { applicationMode, content } = useAppSelector((state) => state.init)
  const { latestFulfilledId } = useAppSelector((state) => state.internalSaveEvent)
  const { apiConfigs } = useAppSelector((state) => state.requests)
  const { requestStatus_fetchPrices_aggregated, requestStatus_addToCart_aggregated } = useAppSelector(
    (state) => state.requests.requestStatus
  )
  const {
    isAddToCartPromptModalOpen,
    isAddToCartConfirmedModalOpen,
    isAddToCartAdditionalPromptModalOpen,
    isAddToCartErrorModalOpen,
    isAddToCartUnavailableModalOpen,
    isMobileMenuOpen,
    isSummaryPreviewExpanded,
  } = useAppSelector((state) => state.ui)

  // true when waiting for savefile from DM iframe
  // **OR** waiting for thunks/addToCart_aggregated promise
  const [isRequestPending, setIsRequestPending] = useState(false)

  const [internalSaveUuidv4, setInternalSaveUuidv4] = useState('')

  const handleAddToCart = (): void => {
    setIsRequestPending(true)
    const { internalSaveUuidv4 } = onRequestSaveProject({ type: CART })
    if (internalSaveUuidv4) setInternalSaveUuidv4(internalSaveUuidv4)
  }

  useEffect(() => {
    if (isRequestPending) {
      if (requestStatus_addToCart_aggregated === FULFILLED) {
        setIsRequestPending(false)

        if (latestFulfilledId === internalSaveUuidv4) {
          dispatch(setIsAddToCartPromptModalOpen(false))
          dispatch(setIsAddToCartConfirmedModalOpen(true))

          if (applicationMode === PRODUCT_PAGE) {
            getCheckoutcart({
              apiConfigs,
              onSuccess: ({ data: html }) => {
                if (window.updateCartQuantity) {
                  // Call function from Scensum to update cart preview in website header
                  window.updateCartQuantity(html)
                } else {
                  console.error('window.updateCartQuantity not found')
                }
              },
            })
          }
        } else {
          console.error('Unexpected error in AddToCart component; latestFulfilledId !== internalSaveUuidv4')
        }
      }

      if (requestStatus_addToCart_aggregated === REJECTED) {
        setIsRequestPending(false)
        dispatch(setIsAddToCartPromptModalOpen(false))
        dispatch(setIsAddToCartErrorModalOpen(true))
      }
    }
  }, [requestStatus_addToCart_aggregated])

  useEffect(() => {
    if (bundleErrors) {
      dispatch(setIsAddToCartUnavailableModalOpen(true))
    }
  }, [bundleErrors])

  return (
    <>
      <Styled.CTA
        applicationMode={applicationMode}
        $insideSummaryPanel={insideSummaryPanel}
        // Adding className to apply Scensum styling
        className={`button-primary${applicationMode === PRODUCT_PAGE ? '-large' : ''}`}
        disabled={requestStatus_fetchPrices_aggregated !== FULFILLED}
        onClick={() => {
          if (isMobileMenuOpen) dispatch(setIsMobileMenuOpen(false))
          if (isSummaryPreviewExpanded) dispatch(setIsSummaryPreviewExpanded(false))

          // If requestStatus_fetchPrices_aggregated is fulfilled but its response contains errors
          if (bundleErrors) {
            dispatch(setIsAddToCartUnavailableModalOpen(true))
            return
          }

          if (requestStatus_addToCart_aggregated === FULFILLED) {
            dispatch(setIsAddToCartAdditionalPromptModalOpen(true))
            return
          }

          dispatch(setIsAddToCartPromptModalOpen(true))
        }}
      >
        {!insideSummaryPanel && applicationMode === DEFAULT && <i className="fas fa-cart-plus" aria-hidden="true" />}
        <span className="button-text">{content.buttons.addToCart}</span>
      </Styled.CTA>

      <Modal
        isOpen={isAddToCartPromptModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartPromptModalOpen(false))
        }}
        contentLabel={content.modals.addToCartPrompt.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content.modals.addToCartPrompt.title}</Heading>
          <p>
            {content.modals.addToCartPrompt.description} {articlesTotal}
            &nbsp;{content.modals.addToCartPrompt.description_2}
          </p>
          <Styled.Button
            className="button-secondary"
            disabled={isRequestPending}
            onClick={() => {
              dispatch(setIsAddToCartPromptModalOpen(false))
            }}
          >
            {content.buttons.cancel}
          </Styled.Button>
          <Styled.Button
            className="button-primary"
            disabled={requestStatus_fetchPrices_aggregated !== FULFILLED || isRequestPending}
            isLoading={isRequestPending}
            onClick={() => {
              handleAddToCart()
            }}
          >
            {content.buttons.addToCart}
          </Styled.Button>
          <ArticlesList isAddToCartPrompt />
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartConfirmedModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartConfirmedModalOpen(false))
        }}
        contentLabel={content.modals.addToCartConfirmed.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content.modals.addToCartConfirmed.title}</Heading>
          <p>
            {content.modals.addToCartConfirmed.description} {articlesTotal}
            &nbsp;{content.modals.addToCartConfirmed.description_2}
          </p>
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartConfirmedModalOpen(false))
            }}
          >
            {content.buttons.close}
          </Styled.Button>
          <Styled.Button
            className="button-primary-arrow"
            id="to-checkout-button"
            onClick={() => {
              window.location.href = `${apiConfigs.scensumBaseUrl}/checkout/`
            }}
          >
            {content.buttons.toCheckout}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartAdditionalPromptModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartAdditionalPromptModalOpen(false))
        }}
        contentLabel={content.modals.addToCartAdditionalPrompt.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content.modals.addToCartAdditionalPrompt.title}</Heading>
          <p>{content.modals.addToCartAdditionalPrompt.description}</p>
          <p>{content.modals.addToCartAdditionalPrompt.description_2}</p>
          <Styled.Button
            className="button-primary"
            onClick={() => {
              dispatch(setIsAddToCartAdditionalPromptModalOpen(false))
            }}
          >
            {content.buttons.cancel}
          </Styled.Button>
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartAdditionalPromptModalOpen(false))
              dispatch(setIsAddToCartPromptModalOpen(true))
            }}
          >
            {content.buttons.proceed}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartErrorModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartErrorModalOpen(false))
        }}
        contentLabel={content.modals.addToCartError.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content.modals.addToCartError.title}</Heading>
          <p>{content.modals.addToCartError.description}</p>
          <p>{content.modals.addToCartError.description_2}</p>
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartErrorModalOpen(false))
            }}
          >
            {content.buttons.close}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
      <Modal
        isOpen={isAddToCartUnavailableModalOpen}
        onRequestClose={() => {
          dispatch(setIsAddToCartUnavailableModalOpen(false))
        }}
        contentLabel={content.modals.addToCartUnavailable.title}
      >
        <Styled.ModalAddToCart>
          <Heading level={2}>{content.modals.addToCartUnavailable.title}</Heading>
          <p>{content.modals.addToCartUnavailable.description}</p>
          <Styled.Button
            className="button-secondary"
            onClick={() => {
              dispatch(setIsAddToCartUnavailableModalOpen(false))
            }}
          >
            {content.buttons.close}
          </Styled.Button>
        </Styled.ModalAddToCart>
      </Modal>
    </>
  )
}

export default AddToCart
